type MigrationBannerLink = {
    name: string;
    url: string;
    icon?: string;
    standard?: string;
};

type MigrationBannerConfig = {
    enabled: boolean;
    headline?: string;
    description?: string;
    notice?: {
        title: string;
        paragraphs: string[];
    };
    network: {
        name: string;
        chain: string;
        rpc: string[];
        cosmosRpc?: string[];
        restApi?: string[];
        faucets: string[];
        nativeCurrency: {
            name: string;
            symbol: string;
            decimals: number;
        };
        features: Array<{ name: string }>;
        infoURL: string;
        shortName: string;
        chainId: number;
        networkId: number;
        icon?: string;
        explorers: MigrationBannerLink[];
    };
};

const getMigrationBanner = (): MigrationBannerConfig | undefined => {
    const value = process.env.REACT_APP_MIGRATION_BANNER;

    if (!value) return undefined;

    try {
        return JSON.parse(value) as MigrationBannerConfig;
    } catch {
        return undefined;
    }
};

const MigrationBanner = () => {
    const banner = getMigrationBanner();

    if (!banner?.enabled || !banner.network) return null;

    const network = banner.network;
    const details = [
        ["Chain", network.chain],
        ["Chain ID", network.chainId],
        ["Network ID", network.networkId],
        ["Short name", network.shortName],
        [
            "Native currency",
            `${network.nativeCurrency.name} (${network.nativeCurrency.symbol}, ${network.nativeCurrency.decimals} decimals)`,
        ],
        ["Features", network.features.map((feature) => feature.name).join(", ")],
    ];

    const linkGroups = [
        { label: "EVM RPC", links: network.rpc.map((url) => ({ name: url, url })) },
        { label: "Cosmos RPC", links: (network.cosmosRpc ?? []).map((url) => ({ name: url, url })) },
        { label: "REST API", links: (network.restApi ?? []).map((url) => ({ name: url, url })) },
        { label: "Faucet", links: network.faucets.map((url) => ({ name: url, url })) },
        { label: "Explorers", links: network.explorers },
        { label: "Network info", links: [{ name: network.infoURL, url: network.infoURL }] },
    ];

    return (
        <aside className="migration-banner" aria-label={banner.notice?.title || banner.headline || `${network.name} migration notice`}>
            <div className="migration-banner__content">
                <div>
                    {banner.notice ? (
                        <div className="migration-banner__notice">
                            <h1>{banner.notice.title}</h1>
                            {banner.notice.paragraphs.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </div>
                    ) : (
                        <>
                            <h1>{banner.headline || `${network.name} migration notice`}</h1>
                            {banner.description ? <p className="migration-banner__description">{banner.description}</p> : null}
                        </>
                    )}
                </div>

                <dl className="migration-banner__details">
                    {details.map(([label, value]) => (
                        <div key={String(label)}>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                        </div>
                    ))}
                </dl>

                <div className="migration-banner__links">
                    {linkGroups.map((group) => (
                        <div key={group.label}>
                            <p>{group.label}</p>
                            {group.links.map((link) => (
                                <a key={`${group.label}-${link.url}`} href={link.url} target="_blank" rel="noreferrer">
                                    {link.name}
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </aside>
    );
};

export default MigrationBanner;
